/**
 * Module for configuring the KB settings as part of the KB creation wizard
 */
define([
  "marionette",
  "underscore",
  "messaging/messenger",
  "./kbSettingsView",
  "modules/accountAdmin/kbAdmin/kbModel",
], function (Marionette, _, Messenger, KbSettingsView, KbModel) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "kbWizardSettings";
      this.region = options.region;
      this.editMode = options.editMode;
    },
    show: function (kb) {
      this.kb = kb;
      this.kb.engineModes = Messenger.reqres.request("kb:engine:modes");
      if (!this.kb.tenants) {
        this.kb.tenants = Messenger.reqres.request("tenants");
      }
      this.kbs = Messenger.reqres.request("admin:kbs");
      this.landlords = Messenger.reqres.request("landlords");

      this.view = new KbSettingsView.kbSettingsView({ model: this.kb });

      this.view.on(
        "toggle:agent:assist",
        function () {
          this.setNavigationButtons();
        },
        this,
      );

      this.view.on(
        "change:tenant",
        function () {
          //If the selected tenant belongs to a landlord, then re-filter the features for that landlord
          // var selectedTenantId = this.view.getTenant();
          // this.kb.set("tenantId", selectedTenantId);
          this.updateFields();
          this.tenant = this.kb.tenants.findWhere({ tenantId: this.view.getTenant() });
          this.kb.landlord = this.landlords.findWhere({
            landlordId: this.tenant.get("landlordId"),
          });
          this.view.model = this.kb;
          this.view.render();
        },
        this,
      );

      //Check if the tenant of this KB belong to a landlord, if so then hide any features not enabled for the landlord
      this.kb.tenants.onSynced(function () {
        if (!this.tenant) {
          if (!this.editMode) {
            const firstTenant = this.kb.tenants.first();
            this.kb.set("tenantId", firstTenant.get("tenantId"));
          }
          this.tenant = this.kb.tenants.findWhere({ tenantId: this.kb.get("tenantId") });
        }
        this.landlords.onSynced(function () {
          this.kb.landlord = this.landlords.findWhere({
            landlordId: this.tenant.get("landlordId"),
          });
          this.view.model = this.kb;
          this.showRegion(this.view);
        }, this);
      }, this);

      kb.on(
        "invalid",
        function (model, error) {
          this.view.clearErrors();
          if (error["kbName"]) {
            this.view.kbNameError(error["kbName"]);
          }
          if (error["kbDescription"]) {
            this.view.kbDescriptionError(error["kbDescription"]);
          }
          if (error["tenant"]) {
            this.view.tenantError(error["tenant"]);
          }
          if (error["taskManagement"]) {
            this.view.taskManagementError(error["taskManagement"]);
          }
          if (error["responseCollaboration"]) {
            this.view.responseCollaborationError(error["responseCollaboration"]);
          }
          if (error["responseVersioning"]) {
            this.view.responseVersioningError(error["responseVersioning"]);
          }
          if (error["responseReview"]) {
            this.view.responseReviewError(error["responseReview"]);
          }
          if (error["exampleGeneration"]) {
            this.view.exampleGenerationError(error["exampleGeneration"]);
          }
          if (error["actionGroups"]) {
            this.view.actionGroupsError(error["actionGroups"]);
          }
          if (error["agentAssist"]) {
            this.view.agentAssistError(error["agentAssist"]);
          }
          if (error["adHocReporting"]) {
            this.view.adHocReportingError(error["adHocReporting"]);
          }
          if (error["recommendations"]) {
            this.view.recommendationsError(error["recommendations"]);
          }
          if (error["crossTouchPointAccess"]) {
            this.view.crossTouchPointAccessError(error["crossTouchPointAccess"]);
          }
          if (error["editSource"]) {
            this.view.editSourceError(error["editSource"]);
          }
          if (error["aiFeatures"]) {
            this.view.aiFeaturesError(error["aiFeatures"]);
          }
          if (error["customLibrarian"]) {
            this.view.customLibrarianError(error["customLibrarian"]);
          }
          if (error["secureUploadAccess"]) {
            this.view.secureUploadAccessError(error["secureUploadAccess"]);
          }
          if (error["siteMapAccess"]) {
            this.view.siteMapAccessError(error["siteMapAccess"]);
          }
          if (error["engineMode"]) {
            this.view.engineModeError(error["engineMode"]);
          }
        },
        this,
      );
    },
    getKb: function () {
      return this.kb;
    },
    setNavigationButtons: function () {
      // Hide the next button if Agent Assist is disabled
      if (this.editMode) {
        if (!this.view.getAgentAssist()) {
          Messenger.vent.trigger("toggle:next:button", false);
          Messenger.vent.trigger("toggle:done:button", true);
        } else {
          Messenger.vent.trigger("toggle:next:button", true);
          // If any of the required PCAS settings are missing, then disable the done button
          if (
            !this.kb.get("pcasURL") ||
            !this.kb.get("company") ||
            !this.kb.get("pcasUsername") ||
            !this.kb.get("pcasPassword")
          ) {
            Messenger.vent.trigger("toggle:done:button", false);
          } else {
            Messenger.vent.trigger("toggle:done:button", true);
          }
        }
      }

      // When hitting back in wizard, view does not have value.  Only change value if view has a value
      if (!_.isUndefined(this.view.getAgentAssist())) {
        Messenger.vent.trigger("show:agent:assist:step", this.view.getAgentAssist());
      }
    },
    updateFields: function () {
      this.kb.set("kbName", this.view.getKbName());
      this.kb.set("kbDescription", this.view.getKbDescription());
      this.kb.set("tenantId", this.view.getTenant());
      this.kb.set("taskManagement", this.view.getTaskManagement());
      this.kb.set("actionGroups", this.view.getActionGroups());
      this.kb.set("agentAssist", this.view.getAgentAssist());
      this.kb.set("adHocReporting", this.view.getAdHocReporting());
      this.kb.set("recommendations", this.view.getRecommendations());
      this.kb.set("crossTouchPointAccess", this.view.getCrossTouchPointAccess());
      this.kb.set("siteMapAccess", this.view.getSiteMapAccess());
      this.kb.set("engineMode", this.view.getEngineMode());
      this.kb.set("responseReview", this.view.getResponseReview());
      this.kb.set("exampleGeneration", this.view.getExampleGeneration());
      this.kb.set("responseCollaboration", this.view.getResponseCollaboration());
      this.kb.set("editSource", this.view.getEditSource());
      this.kb.set("aiFeatures", this.view.getAiFeatures());
      this.kb.set("customLibrarian", this.view.getCustomLibrarian());
      if (this.kb.isNew()) {
        this.kb.set("responseVersioning", this.view.getResponseVersioning());
        this.kb.set("secureUploadAccess", this.view.getSecureUploadAccess());
      }
    },
    isKbNameValid: function () {
      let isValid = true;
      isValid = this.kb.get("kbName").length !== 0 && this.kb.get("kbName").length <= 50;
      this.kbs.each(function (kb) {
        //check if the new KB has the same name (ignore case) and is actually a different object
        if (kb.get("kbName").toLowerCase() === this.kb.get("kbName").toLowerCase()) {
          // When editing, cid will be different
          if (!this.editMode && kb.cid !== this.kb.cid) {
            isValid = false;
          }
        }
      }, this);
      return isValid;
    },
    isValid: function () {
      this.updateFields();
      this.kb.isValid();
      // If validation fails, check if it was the KB name (ignore PCAS fields failing in this step)
      return this.isKbNameValid();
    },
  });
});
